import api from "./axios";
import {
  type StandbyOrder,
  type StandbyOrderSearchFilters,
  mapStandbyOrderResponse,
  mapStandbyOrdersResponse,
} from "../types/standbyOrder";
import {
  type PaginationResponse,
  mapPaginationResponse,
  type PaginationParam,
} from "../types/paginationResponse";
import { buildPaginationQuery } from "../utils/pagination";

export interface SearchStandbyOrdersResponse {
  standbyOrders: StandbyOrder[];
  pagination: PaginationResponse;
}

export const searchStandbyOrdersApi = async (
  filters?: StandbyOrderSearchFilters,
  pagination?: PaginationParam,
): Promise<SearchStandbyOrdersResponse> => {
  const queryString = buildPaginationQuery(pagination);
  const url = `/api/v1/standbyOrders/search${queryString}`;
  const response = await api.post<any>(url, filters ?? {});
  return {
    standbyOrders: mapStandbyOrdersResponse(response.data),
    pagination: mapPaginationResponse(response.data.data.pagination),
  };
};

export const createStandbyOrderApi = async (
  data: Record<string, unknown>,
): Promise<StandbyOrder> => {
  const response = await api.post<any>("/api/v1/standbyOrders", data);
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const getStandbyOrderByIdApi = async (orderId: string): Promise<StandbyOrder> => {
  const response = await api.get<any>(`/api/v1/standbyOrders/${orderId}`);
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const updateStandbyOrderApi = async (
  orderId: string,
  data: Record<string, unknown>,
): Promise<StandbyOrder> => {
  const response = await api.put<any>(`/api/v1/standbyOrders/${orderId}`, data);
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const deleteStandbyOrderApi = async (orderId: string): Promise<void> => {
  await api.delete(`/api/v1/standbyOrders/${orderId}`);
};

export const readyForReviewStandbyOrderApi = async (orderId: string): Promise<StandbyOrder> => {
  const response = await api.post<any>(`/api/v1/standbyOrders/${orderId}/readyForReview`, {});
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const reopenStandbyOrderApi = async (orderId: string): Promise<StandbyOrder> => {
  const response = await api.post<any>(`/api/v1/standbyOrders/${orderId}/reopen`, {});
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const submitRevisionStandbyOrderApi = async (
  orderId: string,
  note?: string,
): Promise<StandbyOrder> => {
  const response = await api.post<any>(`/api/v1/standbyOrders/${orderId}/submitRevision`, {
    note,
  });
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const requestRevisionStandbyOrderApi = async (
  orderId: string,
  note: string,
): Promise<StandbyOrder> => {
  const response = await api.post<any>(`/api/v1/standbyOrders/${orderId}/requestRevision`, {
    note,
  });
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const verifyStandbyOrderApi = async (orderId: string): Promise<StandbyOrder> => {
  const response = await api.post<any>(`/api/v1/standbyOrders/${orderId}/verify`, {});
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const approveStandbyOrderApi = async (orderId: string): Promise<StandbyOrder> => {
  const response = await api.post<any>(`/api/v1/standbyOrders/${orderId}/approve`, {});
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const approveStandbyOrderWithOverrideApi = async (
  orderId: string,
  data: { expenseOverrides: Array<{ id: string; overrideAmount: number }>; overrideNotes?: string },
): Promise<StandbyOrder> => {
  const response = await api.post<any>(
    `/api/v1/standbyOrders/${orderId}/approveWithOverride`,
    data,
  );
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};

export const updateStandbyExpenseReviewChecklistApi = async (
  orderId: string,
  data: { key: string; checked: boolean },
): Promise<StandbyOrder> => {
  const response = await api.put<any>(
    `/api/v1/standbyOrders/${orderId}/expenseReviewChecklist`,
    data,
  );
  return mapStandbyOrderResponse(response.data.data.standbyOrder);
};
